/* AppraisalComponents/helpers.js — Phase C · C4a shell extraction.
 *
 * Pure helper functions used across the Components tab surface. Zero
 * React state · zero side effects. Extracted from AppraisalComponentsTab.js
 * (2026-02c) as part of the C4a shell cut · safe to import from any
 * per-work-type subform (C4b/c/d).
 *
 * Contents:
 *   • fmt                 — currency formatter
 *   • buildSheetProfileMap— parse "Corrugate 0.76m" labels into map
 *   • isMitreLine         — [MITRE] tag detector
 *   • isAutoBracketLine   — [AUTO:*] tag detector
 *   • stripTag            — remove a tag prefix from `notes`
 *   • withMitreTag        — toggle [MITRE] prefix on `notes`
 *   • lineTotals          — quantity × length × rate math
 *   • inferTypeName       — pick a rate row by material + subtype tokens
 */
import { MITRE_TAG } from './constants';

export const fmt = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Sheet profile labels are seeded as "Corrugate 0.76m" etc. — split each label
// into a plain name and its coverage so the UI can show a deduped name list
// ("Corrugate") and offer the cover separately. Falls back gracefully when
// there's no number (e.g. "hip cuts").
export function buildSheetProfileMap(labels) {
  const map = {};
  (labels || []).forEach(label => {
    const m = label.match(/^(.*?)\s*(\d+(?:\.\d+)?)\s*m?\s*$/);
    if (m) {
      const name = m[1].trim();
      const cov = parseFloat(m[2]);
      if (!map[name]) map[name] = [];
      if (!map[name].includes(cov)) map[name].push(cov);
    } else if (!map[label]) {
      map[label] = [];
    }
  });
  Object.values(map).forEach(a => a.sort((x, y) => x - y));
  return map;
}

// Iter149 — Notes-tag detectors + writers.
export const isMitreLine = (l) =>
  /^\[MITRE\]/i.test((l && l.notes) || '');

export const isAutoBracketLine = (l) =>
  /^\[AUTO:(bracket|dropper|stop_end|mitre|rivet|silicone|silicone_modified|silicone_clear|extra_clips|multidek_clips|multidek_screws|bedding_mortar_ridge|bedding_mortar_barge|pointing_compound_ridge|pointing_compound_barge|foam_filler|edging_flashguard|edging_ali)/i
    .test((l && l.notes) || '');

export const stripTag = (notes, tag) =>
  (notes || '').replace(
    new RegExp(`^\\s*${tag.replace(/[[\]]/g, '\\$&')}\\s*`, 'i'),
    '',
  );

export const withMitreTag = (notes, on) => on
  ? `${MITRE_TAG} ${stripTag(notes, MITRE_TAG)}`.trim()
  : stripTag(notes, MITRE_TAG).trim();

// Iter150 — Line-total math. Mirrors backend `_calc_line_totals`.
//   • base = length > 0 ? qty × length : qty  (legacy fallback)
//   • mitre_extra = clamp(0..2, mitre_count) × mitreExtraM
//   • customer_charge = total × (materials_rate + labour_rate)
//   • cost_to_us      = total × (buy_cost + contractor_rate)
export function lineTotals(line, mitreExtraM = 0) {
  const qty = Number(line.quantity) || 0;
  const length = Number(line.length) || 0;
  // iter313f — Face-pin area-only override. When set, the operator
  // entered a raw m² on the pin instead of L+W; [Total] shows that
  // area directly instead of qty×length. Cleared by setting to 0.
  const override = Number(line.total_m2_override) || 0;
  const base = override > 0
    ? override
    : (length > 0 ? qty * length : qty);
  const mitreCount = Math.max(0, Math.min(2, Number(line.mitre_count) || 0));
  const extra = mitreCount * (Number(mitreExtraM) || 0);
  const total = base + extra;
  const buy = Number(line.buy_cost) || 0;
  const mat = Number(line.materials_rate) || 0;
  const lab = Number(line.labour_rate) || 0;
  const con = Number(line.contractor_rate) || 0;
  return {
    line_total: total,
    base_length: base,
    mitre_extra_length: extra,
    customer_charge: total * (mat + lab),
    cost_to_us: total * (buy + con),
    materials_profit: total * (mat - buy),
    labour_profit: total * (lab - con),
  };
}

// Pick a rate-row whose name reflects the operator's Roof Subtype +
// Material. Returns the rate `type_name` so the caller can drop it
// straight into the line.
//   inferTypeName(rates, 'Modern', 'Bed & Point') → 'Bed and Point Modern'
// Jun 2026 — single implementation (with roof_type_links guard) lives in the shared row helpers.
export { inferTypeName, isGutterLengthLine, spoutingRowsFor, spoutRatePatch } from '@/components/shared/ComponentRow/helpers';
